import {
  Box,
  Text,
  Button,
  HStack,
  Heading,
  Icon,
  Image,
  Input,
} from "@chakra-ui/react";
import {
  MdPhotoCamera as CameraIcon,
  MdMic as VoiceIcon,
  MdPlayCircle as SocialIcon,
  MdLink as URLIcon,
  MdCollectionsBookmark as CollectionIcon,
  MdShare as ShareIcon,
  MdMenuBook as GuideIcon,
  MdSearch as SearchIcon,
  MdCheckCircle as CheckIcon,
  MdArrowForward as ArrowIcon,
  MdWarningAmber as WarningIcon,
} from "react-icons/md";
import { motion, type Variants } from "framer-motion";
import logoIcon from '@/assets/logo-icon.png';
import { useState, useEffect } from "react";
import { Link } from "wouter";
import { StatusBar, Style } from "@capacitor/status-bar";
import { getApiUrlAsync } from "@/lib/apiConfig";
import { useAuth } from "@/hooks/useAuth";
import { trackWaitlistSignup } from "@/lib/analytics";
import type { OAuthError } from "@auth0/auth0-react";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

function ClaudeIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z"/>
    </svg>
  );
}

function AppLogoIcon() {
  return <img src={logoIcon} alt="" style={{ width: '28px', height: '28px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />;
}

function scrollToSignup(e: React.MouseEvent) {
  e.preventDefault();
  document.getElementById("early-access")?.scrollIntoView({ behavior: "smooth" });
}

/* ──────────────────────────────────────────────
   DATA
   ────────────────────────────────────────────── */

const importMethods = [
  { icon: URLIcon,    label: "Any recipe website",     desc: "Paste a URL from any food site or blog. We extract just the recipe — ingredients, steps, and how to make it." },
  { icon: SocialIcon, label: "Instagram & TikTok",     desc: "That reel you bookmarked and forgot about. Drop in the link and we'll extract the recipe before it disappears." },
  { icon: CameraIcon, label: "Old photos & cookbooks", desc: "Snap a photo of a cookbook page, handwritten card, or yellowed clipping. We turn it into a clean, searchable recipe." },
  { icon: VoiceIcon,  label: "Something you invented", desc: "Made something up? Type it or dictate it. We'll structure it properly so you can actually make it again." },
];

const appFeatures = [
  { icon: CollectionIcon, title: "Smart collections",       desc: "Organise recipes by cuisine, diet, occasion, or whatever you like — then generate a shopping list from the whole collection in one tap.", accent: "#40798C" },
  { icon: AppLogoIcon,    title: "AI Chef Chat",            desc: "Ask your personal AI chef anything — substitutions, scaling, technique tips — and it already knows which recipe you're looking at.", accent: "#C85A1E" },
  { icon: GuideIcon,      title: "Hands-free cook mode",    desc: "Flip on Cook mode and your screen stays awake while you cook. No more unlocking your phone with floury fingers.", accent: "#2F4B26" },
  { icon: ClaudeIcon,     title: "Claude integration",      desc: "Connect your pantry directly to Claude. Ask about any recipe, plan your week, or get substitution ideas — your whole collection in context.", accent: "#D97757" },
  { icon: ShareIcon,      title: "Share with anyone",       desc: "Send any recipe as a link — no account needed to preview it. Friends and family see the full recipe, ready to cook.", accent: "#C85A1E" },
  { icon: SearchIcon,     title: "Search & filter",         desc: "Find any recipe instantly by name or keyword. Filter by cuisine, diet, difficulty, equipment, and more.", accent: "#2F4B26" },
];

const sampleRecipes = [
  { src: "https://images.unsplash.com/photo-1511690656952-34342bb7c2f2?auto=format&fit=crop&w=400&q=80", name: "Spaghetti Carbonara",     tag: "Italian",   meta: "25 min · 4 serves" },
  { src: "https://images.unsplash.com/photo-1504754524776-8f4f37790ca0?auto=format&fit=crop&w=400&q=80", name: "Blueberry Pancakes",      tag: "Breakfast", meta: "20 min · 2 serves" },
  { src: "https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?auto=format&fit=crop&w=400&q=80", name: "Thai Green Curry",         tag: "Thai",      meta: "35 min · 3 serves" },
  { src: "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=400&q=80", name: "Sourdough Loaf",           tag: "Baking",    meta: "3 hrs · 8 serves"  },
  { src: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?auto=format&fit=crop&w=400&q=80", name: "Chicken Tikka Masala",     tag: "Indian",    meta: "45 min · 4 serves" },
  { src: "https://images.unsplash.com/photo-1525351484163-7529414344d8?auto=format&fit=crop&w=400&q=80", name: "Smashed Avocado Toast",    tag: "Breakfast", meta: "10 min · 1 serve"  },
];

const TOTAL_TRIAL_SPOTS = 50;
const WAITLIST_ENABLED = true;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" } }),
};

/* ──────────────────────────────────────────────
   COMPONENT
   ────────────────────────────────────────────── */

export default function Landing() {
  const { login, error: authError } = useAuth();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [successVariant, setSuccessVariant] = useState<"spot" | "overflow" | "duplicate">("spot");
  const [errorMsg, setErrorMsg] = useState("");
  const [spotsRemaining, setSpotsRemaining] = useState<number | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    StatusBar.setOverlaysWebView({ overlay: true });
    StatusBar.setStyle({ style: Style.Dark });
    return () => {
      StatusBar.setStyle({ style: Style.Dark });
      StatusBar.setBackgroundColor({ color: '#FDFDFB' });
      StatusBar.setOverlaysWebView({ overlay: false });
    };
  }, []);

  useEffect(() => {
    const isAccessDenied = !!authError && (authError as OAuthError).error === "access_denied";
    if (isAccessDenied) setIsBlocked(true);

    // Always fetch count on load so the early-access section copy stays accurate
    getApiUrlAsync("/waitlist")
      .then((url) => fetch(url))
      .then((res) => res.json())
      .then((data) => setSpotsRemaining(data.spotsRemaining ?? 0))
      .catch(() => setSpotsRemaining(0)); // fallback: assume full
  }, [authError]);

  async function handleSignup(e: React.FormEvent) {
    e.preventDefault();
    if (!WAITLIST_ENABLED || !email.trim()) return;
    setStatus("loading");
    setErrorMsg("");

    try {
      const url = await getApiUrlAsync("/waitlist");
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // Duplicate email — treat as success with a friendly message
        if (res.status === 409) {
          setSuccessVariant("duplicate");
          setStatus("success");
          return;
        }
        throw new Error(body.message || "Something went wrong. Please try again.");
      }
      setSuccessVariant(spotsRemaining === 0 ? "overflow" : "spot");
      trackWaitlistSignup();
      setStatus("success");
    } catch (err: unknown) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  }

  return (
    <Box bg={{ base: "bg.muted", md: "bg.canvas" }} minH="100vh">
    <Box id="main" bg="bg.surface" minH="100vh" display="flex" flexDir="column" maxW={{ base: "480px", md: "none" }} mx="auto" boxShadow={{ base: "0 0 40px rgba(0,0,0,0.08)", md: "none" }}>
      {/* ══════════════════════════════════════════
          HERO — full-bleed dark with food imagery
          ══════════════════════════════════════════ */}
      <Box
        position="relative"
        overflow="hidden"
        bg="#2D2926"
        minH={{ base: "auto", md: "520px" }}
      >
        {/* Background food collage (faded) */}
        <Box
          position="absolute"
          inset={0}
          display="grid"
          gridTemplateColumns="repeat(4, 1fr)"
          opacity={0.18}
        >
          {[
            "https://images.unsplash.com/photo-1504754524776-8f4f37790ca0?auto=format&fit=crop&w=500&q=60",
            "https://images.unsplash.com/photo-1506354666786-959d6d497f1a?auto=format&fit=crop&w=500&q=60",
            "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=500&q=60",
            "https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?auto=format&fit=crop&w=500&q=60",
          ].map((src, i) => (
            <Image key={i} src={src} alt="" w="100%" h="100%" objectFit="cover" />
          ))}
        </Box>
        {/* Gradient overlay */}
        <Box position="absolute" inset={0} bg="linear-gradient(180deg, rgba(45,41,38,0.6) 0%, rgba(45,41,38,0.95) 70%, #2D2926 100%)" />

        {/* Content */}
        <Box position="relative" zIndex={1} px={{ base: 5, md: 10, lg: 16 }} pt={{ base: "calc(env(safe-area-inset-top, 0px) + 20px)", md: 6 }} pb={{ base: 10, md: 14 }}>
          {/* Top bar (inside hero) */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={{ base: 10, md: 14 }}>
            <HStack gap={2}>
              <img src={logoIcon} alt="" style={{ height: '26px', width: 'auto' }} />
              <Heading fontFamily="heading" fontWeight="800" fontSize="18px" color="#F7F6F0">
                Byte <Text as="span" color="#D97A44">Pantry</Text>
              </Heading>
            </HStack>
            <HStack gap={2}>
              <Button
                size="sm"
                bg="transparent"
                border="1px solid"
                borderColor="rgba(247,246,240,0.2)"
                color="#F7F6F0"
                fontFamily="body"
                fontWeight="500"
                fontSize="12px"
                borderRadius="full"
                px={4}
                h="32px"
                _hover={{ bg: "rgba(247,246,240,0.08)" }}
                onClick={scrollToSignup}
              >
                Join the waitlist
              </Button>
              <Button
                size="sm"
                bg="transparent"
                color="#F7F6F0"
                fontFamily="body"
                fontWeight="500"
                fontSize="12px"
                borderRadius="full"
                px={3}
                h="32px"
                opacity={0.5}
                _hover={{ opacity: 0.85, bg: "rgba(247,246,240,0.08)" }}
                onClick={() => login("/")}
              >
                Log in
              </Button>
            </HStack>
          </Box>

          {/* Blocked email notice */}
          {isBlocked && (
            <Box
              bg="rgba(200,90,30,0.15)"
              border="1px solid rgba(200,90,30,0.35)"
              borderRadius="14px"
              px={5}
              py={4}
              mb={6}
              maxW={{ md: "680px" }}
              mx={{ md: "auto" }}
              display="flex"
              alignItems="flex-start"
              gap={3}
            >
              <Icon as={WarningIcon} color="#D97A44" boxSize={5} flexShrink={0} mt="2px" />
              <Box>
                <Text fontFamily="heading" fontWeight="700" fontSize="14px" color="#F7F6F0" mb={1}>
                  Beta access is currently restricted
                </Text>
                <Text fontFamily="body" fontWeight="300" fontSize="13px" color="#F7F6F0" opacity={0.7} mb={3} lineHeight="1.6">
                  {spotsRemaining === null || spotsRemaining === 0
                    ? "Your email isn't on our beta access list. The waitlist is full right now, but sign up anyway — there may be spots available in the future and we'll let you know if one opens up."
                    : "Your email isn't on our beta access list. There are still spots on the waitlist — join below and we'll be in touch when it's time to claim yours."}
                </Text>
                <Button
                  size="sm"
                  bg="#C85A1E"
                  color="white"
                  fontFamily="heading"
                  fontWeight="700"
                  fontSize="13px"
                  borderRadius="10px"
                  h="36px"
                  px={5}
                  _hover={{ bg: "#A84817" }}
                  onClick={scrollToSignup}
                >
                  Join the waitlist
                  <Icon as={ArrowIcon} boxSize={4} ml={1} />
                </Button>
              </Box>
            </Box>
          )}

          {/* Hero text */}
          <Box maxW={{ md: "1200px" }} mx={{ md: "auto" }} textAlign="center">
            <MotionBox initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Text fontFamily="body" fontWeight="500" fontSize="12px" color="#D97A44" textTransform="uppercase" letterSpacing="0.12em" mb={4}>
                Coming soon to iOS &amp; Android
              </Text>
            </MotionBox>

            <MotionHeading
              fontFamily="heading"
              fontWeight="800"
              fontSize={{ base: "34px", md: "52px", lg: "62px" }}
              lineHeight="1.08"
              color="#F7F6F0"
              mb={5}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              A home for the recipes{" "}
              <Text as="span" color="#40798C" fontFamily="body" fontWeight="300" fontStyle="italic">
                you actually cook
              </Text>
            </MotionHeading>

            <MotionBox initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}>
              <Text
                fontFamily="body"
                fontWeight="300"
                fontSize={{ base: "15px", md: "18px" }}
                lineHeight="1.7"
                color="#F7F6F0"
                opacity={0.55}
                mb={8}
                maxW="560px"
                mx="auto"
              >
                Capture from the web, a photo, or your voice. Organised, searchable, and always with you — even offline.
              </Text>
            </MotionBox>

            <MotionBox initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, delay: 0.35 }}>
              <Button
                bg="#C85A1E"
                color="white"
                fontFamily="heading"
                fontWeight="800"
                fontSize={{ base: "15px", md: "16px" }}
                borderRadius="14px"
                h={{ base: "52px", md: "56px" }}
                px={10}
                onClick={scrollToSignup}
                boxShadow="0 4px 20px rgba(200,90,30,0.35)"
                _hover={{ bg: "#A84817", transform: "translateY(-1px)", boxShadow: "0 6px 24px rgba(200,90,30,0.4)" }}
                _active={{ bg: "#C85A1E", transform: "translateY(0)" }}
                transition="all 0.2s"
                mb={3}
              >
                Claim your free-for-life spot
                <Icon as={ArrowIcon} boxSize={5} ml={2} />
              </Button>
              <Text fontFamily="body" fontSize="13px" color="#F7F6F0" opacity={0.4}>
                First {TOTAL_TRIAL_SPOTS} members get free membership forever
              </Text>
            </MotionBox>
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════
          IMPORT METHODS — horizontal strip
          ══════════════════════════════════════════ */}
      <Box bg="bg.surface" borderBottom="1px solid" borderColor="border.muted">
        <Box
          maxW={{ md: "1200px" }}
          mx={{ md: "auto" }}
          w="100%"
          px={{ base: 5, md: 10, lg: 16 }}
          py={{ base: 8, md: 10 }}
        >
          <Heading
            fontFamily="heading"
            fontWeight="800"
            fontSize={{ base: "18px", md: "22px" }}
            color="text.primary"
            mb={6}
            textAlign="center"
          >
            Save recipes from anywhere
          </Heading>
          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }}
            gap={{ base: 4, md: 5 }}
          >
            {importMethods.map((f, i) => (
              <MotionBox
                key={f.label}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                variants={fadeUp}
                textAlign="center"
                p={{ base: 4, md: 5 }}
                bg="bg.muted"
                borderRadius="16px"
                border="1px solid"
                borderColor="border.muted"
              >
                <Box
                  bg="accent.secondarySubtle"
                  borderRadius="12px"
                  w="48px" h="48px"
                  display="flex" alignItems="center" justifyContent="center"
                  mx="auto"
                  mb={3}
                >
                  <Icon as={f.icon} boxSize={6} color="accent.secondary" />
                </Box>
                <Text fontFamily="heading" fontWeight="700" fontSize={{ base: "14px", md: "15px" }} color="text.primary" mb={1}>
                  {f.label}
                </Text>
                <Text fontFamily="body" fontWeight="300" fontSize={{ base: "12px", md: "13px" }} lineHeight="1.55" color="text.muted">
                  {f.desc}
                </Text>
              </MotionBox>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════
          FEATURES — big, bold, alternating layout
          ══════════════════════════════════════════ */}
      <Box bg="bg.muted">
        <Box
          maxW={{ md: "1200px" }}
          mx={{ md: "auto" }}
          w="100%"
          px={{ base: 5, md: 10, lg: 16 }}
          py={{ base: 10, md: 14 }}
        >
          <Text fontFamily="body" fontWeight="600" fontSize="11px" color="accent.secondary" textTransform="uppercase" letterSpacing="0.1em" textAlign="center" mb={2}>
            What you can do
          </Text>
          <Heading
            fontFamily="heading"
            fontWeight="800"
            fontSize={{ base: "22px", md: "28px" }}
            color="text.primary"
            mb={10}
            textAlign="center"
          >
            Everything you need to cook with confidence
          </Heading>

          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }}
            gap={{ base: 4, md: 5 }}
          >
            {appFeatures.map((f, i) => (
              <MotionBox
                key={f.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                variants={fadeUp}
                p={{ base: 5, md: 6 }}
                bg="bg.surface"
                borderRadius="16px"
                border="1px solid"
                borderColor="border.muted"
                boxShadow="0 1px 4px rgba(0,0,0,0.04)"
                _hover={{ boxShadow: "0 4px 16px rgba(0,0,0,0.08)", transform: "translateY(-2px)" }}
                style={{ transition: "all 0.25s ease" }}
                cursor="default"
              >
                <Box
                  borderRadius="14px"
                  w="56px" h="56px"
                  display="flex" alignItems="center" justifyContent="center"
                  mb={4}
                  mx="auto"
                  bg={f.accent}
                  opacity={0.9}
                >
                  <Icon as={f.icon} boxSize={7} color="white" />
                </Box>
                <Text fontFamily="heading" fontWeight="800" fontSize="15px" color="text.primary" mb={2} textAlign="center">
                  {f.title}
                </Text>
                <Text fontFamily="body" fontWeight="300" fontSize="13px" lineHeight="1.6" color="text.primary" opacity={0.6} textAlign="center">
                  {f.desc}
                </Text>
              </MotionBox>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════
          RECIPE SHOWCASE
          ══════════════════════════════════════════ */}
      <Box bg="bg.surface" borderTop="1px solid" borderColor="border.muted">
        <Box
          maxW={{ md: "1200px" }}
          mx={{ md: "auto" }}
          w="100%"
          px={{ base: 5, md: 10, lg: 16 }}
          py={{ base: 10, md: 14 }}
        >
          <Heading
            fontFamily="heading"
            fontWeight="800"
            fontSize={{ base: "18px", md: "22px" }}
            color="text.primary"
            mb={2}
            textAlign="center"
          >
            Your entire collection, always with you
          </Heading>
          <Text
            fontFamily="body"
            fontWeight="300"
            fontSize="14px"
            color="text.muted"
            textAlign="center"
            mb={8}
          >
            Every recipe you've ever loved, organised and searchable — not buried in a browser tab.
          </Text>
          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }}
            gap={3}
          >
            {sampleRecipes.map((r, i) => (
              <MotionBox
                key={r.name}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-30px" }}
                variants={fadeUp}
              >
                <HStack
                  gap={3}
                  p={3}
                  bg="bg.muted"
                  border="1px solid"
                  borderColor="border.muted"
                  borderRadius="14px"
                >
                  <Box w="72px" h="72px" borderRadius="10px" overflow="hidden" flexShrink={0}>
                    <Image src={r.src} alt={r.name} w="100%" h="100%" objectFit="cover" loading="lazy" />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text fontFamily="heading" fontWeight="800" fontSize="13px" color="text.primary" mb="4px" lineHeight="1.3">
                      {r.name}
                    </Text>
                    <Box display="inline-block" bg="accent.secondarySubtle" borderRadius="full" px={2} py="1px" mb="4px">
                      <Text fontFamily="body" fontWeight="500" fontSize="10px" color="accent.secondary">{r.tag}</Text>
                    </Box>
                    <Text fontFamily="body" fontWeight="400" fontSize="11px" color="text.muted" display="block">{r.meta}</Text>
                  </Box>
                </HStack>
              </MotionBox>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════
          EARLY ACCESS SIGNUP
          ══════════════════════════════════════════ */}
      <Box id="early-access" bg="#2D2926" position="relative" overflow="hidden">
        {/* Decorative glow */}
        <Box position="absolute" top="-120px" left="50%" transform="translateX(-50%)" w="600px" h="300px" bg="radial-gradient(ellipse, rgba(200,90,30,0.15) 0%, transparent 70%)" pointerEvents="none" />

        <Box
          position="relative"
          maxW={{ md: "1200px" }}
          mx={{ md: "auto" }}
          w="100%"
          px={{ base: 5, md: 10, lg: 16 }}
          py={{ base: 14, md: 20 }}
          textAlign="center"
        >
          <HStack gap={2} justifyContent="center" mb={3}>
            <Box bg="#C85A1E" borderRadius="full" w="8px" h="8px" />
            <Text fontFamily="body" fontWeight="600" fontSize="11px" color="#D97A44" textTransform="uppercase" letterSpacing="0.08em">
              {spotsRemaining === 0 ? "Waitlist" : "Limited early access"}
            </Text>
          </HStack>
          <Heading fontFamily="heading" fontWeight="800" fontSize={{ base: "26px", md: "36px" }} color="#F7F6F0" lineHeight="1.15" mb={4}>
            {spotsRemaining === 0
              ? <>The waitlist is <Text as="span" color="#D97A44">currently full</Text></>
              : <>First {TOTAL_TRIAL_SPOTS} users get{" "}<Text as="span" color="#D97A44">free membership for life</Text></>
            }
          </Heading>
          <Text fontFamily="body" fontWeight="300" fontSize={{ base: "14px", md: "16px" }} color="#F7F6F0" opacity={0.5} mb={10} maxW="520px" mx="auto" lineHeight="1.7">
            {spotsRemaining === 0
              ? "We're not taking new beta members right now, but sign up below and we'll let you know if a spot opens up."
              : "Byte Pantry is in beta. Claim a spot, and when we invite you in we'll send a short feedback form. Complete it with genuine thoughts and your membership is free — forever. No credit card needed."
            }
          </Text>

          {status === "success" ? (
            <MotionBox initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
              <Box bg="rgba(47,75,38,0.25)" borderRadius="16px" p={7} maxW="420px" mx="auto" border="1px solid rgba(47,75,38,0.3)">
                <Icon as={CheckIcon} boxSize={8} color="#D97A44" mb={3} />
                <Text fontFamily="heading" fontWeight="700" fontSize="18px" color="#F7F6F0" mb={1}>
                  {successVariant === "overflow" ? "You're signed up!" : "You're on the list!"}
                </Text>
                <Text fontFamily="body" fontWeight="300" fontSize="14px" color="#F7F6F0" opacity={0.6}>
                  {successVariant === "overflow"
                    ? "There may be spots available in the future — we'll let you know if one opens up."
                    : successVariant === "duplicate"
                    ? "This email is already on the waitlist — we'll be in touch."
                    : "We'll email you when it's time to claim your spot."}
                </Text>
              </Box>
            </MotionBox>
          ) : (
            <form
              onSubmit={handleSignup}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}
            >
              <Box display="flex" flexDir={{ base: "column", sm: "row" }} gap={3} w="100%" maxW="600px">
                <Input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  background="#3A3330"
                  border="1px solid"
                  borderColor="rgba(247,246,240,0.18)"
                  borderRadius="12px"
                  color="#F7F6F0"
                  fontFamily="body"
                  fontSize="15px"
                  h="52px"
                  px={5}
                  flex={1}
                  _placeholder={{ color: "rgba(247,246,240,0.35)" }}
                  _focus={{ borderColor: "#D97A44", outline: "none", boxShadow: "0 0 0 2px rgba(217,122,68,0.25)", background: "#3A3330" }}
                  _autofill={{ boxShadow: "0 0 0 1000px #3A3330 inset", WebkitTextFillColor: "#F7F6F0" }}
                  disabled={!WAITLIST_ENABLED || status === "loading"}
                />
                <Button
                  type="submit"
                  data-testid="waitlist-submit"
                  bg="#C85A1E"
                  color="white"
                  fontFamily="heading"
                  fontWeight="700"
                  fontSize="15px"
                  borderRadius="12px"
                  h="52px"
                  px={8}
                  flexShrink={0}
                  boxShadow="0 4px 16px rgba(200,90,30,0.35)"
                  _hover={{ bg: "#A84817" }}
                  _active={{ bg: "#C85A1E" }}
                  disabled={!WAITLIST_ENABLED || status === "loading"}
                >
                  {!WAITLIST_ENABLED ? "Coming soon" : status === "loading" ? "Joining..." : "Join the waitlist"}
                </Button>
              </Box>
              {status === "error" && (
                <Text fontFamily="body" fontSize="12px" color="#EF4444">
                  {errorMsg}
                </Text>
              )}
            </form>
          )}

          {/* Store badges */}
          <HStack gap={3} justifyContent="center" mt={8}>
            <Image
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              h={{ base: "38px", md: "42px" }}
              opacity={0.5}
            />
            <Image
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              h={{ base: "38px", md: "42px" }}
              opacity={0.5}
            />
          </HStack>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════
          FOOTER
          ══════════════════════════════════════════ */}
      <Box bg="#252220" px={{ base: 5, md: 10, lg: 16 }} pt={8} pb={6}>
        <Box maxW={{ md: "1200px" }} mx={{ md: "auto" }} w="100%">
          <HStack gap={2} mb={2}>
            <img src={logoIcon} alt="Byte Pantry" style={{ height: '20px', width: 'auto' }} />
            <Heading fontFamily="heading" fontWeight="800" fontSize="15px" color="#F7F6F0">
              Byte <Text as="span" color="#D97A44">Pantry</Text>
            </Heading>
          </HStack>
          <Text fontFamily="body" fontWeight="300" fontSize="12px" color="#F7F6F0" opacity={0.35} mb={4}>
            Every recipe, always within reach.
          </Text>
          <Box borderTop="1px solid" borderColor="rgba(247,246,240,0.08)" pt={4}>
            <HStack gap={5} mb={2}>
              {[{ label: "Privacy", href: "/privacy" }, { label: "Terms", href: "/terms" }].map((l) => (
                <Link key={l.label} href={l.href} style={{ textDecoration: 'none' }}>
                  <Text fontFamily="body" fontWeight="400" fontSize="12px" color="#F7F6F0" opacity={0.35} _hover={{ opacity: 0.7 }} cursor="pointer">
                    {l.label}
                  </Text>
                </Link>
              ))}
            </HStack>
            <Text fontFamily="body" fontWeight="300" fontSize="11px" color="#F7F6F0" opacity={0.2}>
              &copy; 2026 Byte Pantry. All rights reserved.
            </Text>
          </Box>
        </Box>
      </Box>

    </Box>
    </Box>
  );
}
